import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type {
  DeleteRateLimitResponse,
  EffectiveRateLimitResponse,
  GetGlobalRateLimitResponse,
  ListAllOrgTaskAdmissionOverridesResponse,
  ListOrgRateLimitsResponse,
  ListOrgSkuRateLimitsResponse,
  ListRateLimitRefusalsResponse,
  ListRoutingLoadScopeSettingsResponse,
  ListSkuRateLimitsResponse,
  OrgEffectiveRateLimitResponse,
  PutGlobalRateLimitResponse,
  PutOrgRateLimitResponse,
  PutOrgSkuRateLimitResponse,
  PutRateLimitRequest,
  PutRoutingLoadScopeSettingsResponse,
  PutSkuRateLimitResponse,
  QuotaBypassState,
  RoutingLoadOverviewResponse,
  RoutingLoadScopeKind,
  RoutingLoadSettingsResponse,
  UpdateRoutingLoadScopeSettingsRequest,
  UpdateRoutingLoadSettingsRequest,
  UpdateRoutingLoadSettingsResponse,
} from '@nadoutong/contracts';
import { useSession } from '../hooks/useSession.tsx';
import {
  deleteGlobalRateLimit,
  deleteOrgRateLimit,
  deleteOrgSkuRateLimit,
  deleteRoutingLoadScopeSettings,
  deleteSkuRateLimit,
  getEffectiveRateLimit,
  getGlobalRateLimit,
  getOrgEffectiveRateLimits,
  getQuotaBypass,
  getRoutingLoadOverview,
  getRoutingLoadSettings,
  listAllTaskAdmissionOverrides,
  listAllOrgSkuRateLimits,
  listOrgRateLimits,
  listOrgSkuRateLimits,
  listRateLimitRefusals,
  listRoutingLoadScopeSettings,
  listSkuRateLimits,
  putGlobalRateLimit,
  putOrgRateLimit,
  putOrgSkuRateLimit,
  putQuotaBypass,
  putRoutingLoadScopeSettings,
  putSkuRateLimit,
  updateRoutingLoadSettings,
} from './routingLoad.ts';

export const ROUTING_LOAD_SETTINGS_KEY = ['routing-load-settings'] as const;
export const ROUTING_LOAD_OVERVIEW_KEY = ['routing-load-overview'] as const;
export const ROUTING_LOAD_SCOPE_SETTINGS_KEY = ['routing-load-scope-settings'] as const;
export const ROUTING_LOAD_SKU_RATE_LIMITS_KEY = ['routing-load-rate-limits', 'sku'] as const;
export const QUOTA_BYPASS_KEY = ['quota-bypass'] as const;
export const QUOTA_ORG_RATE_LIMITS_KEY = ['routing-load-rate-limits', 'org-all'] as const;
export const QUOTA_ORG_SKU_ALL_KEY = ['routing-load-rate-limits', 'org-sku'] as const;
export const QUOTA_GLOBAL_RATE_LIMIT_KEY = ['routing-load-rate-limits', 'global'] as const;
export const QUOTA_TASK_ADMISSION_KEY = ['routing-load-task-admission'] as const;
export const QUOTA_REFUSALS_KEY = ['routing-load-refusals'] as const;

const ROUTING_LOAD_OVERVIEW_REFETCH_INTERVAL_MS = 15_000;

export function routingLoadOrgRateLimitsKey(orgId: string): readonly ['routing-load-rate-limits', 'org', string] {
  return ['routing-load-rate-limits', 'org', orgId];
}

export function useRoutingLoadSettings(): UseQueryResult<RoutingLoadSettingsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ROUTING_LOAD_SETTINGS_KEY,
    queryFn: ({ signal }) => getRoutingLoadSettings(fetchImpl, signal),
  });
}

export function useRoutingLoadOverview(): UseQueryResult<RoutingLoadOverviewResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ROUTING_LOAD_OVERVIEW_KEY,
    queryFn: ({ signal }) => getRoutingLoadOverview(fetchImpl, signal),
    refetchInterval: ROUTING_LOAD_OVERVIEW_REFETCH_INTERVAL_MS,
  });
}

export function useUpdateRoutingLoadSettings(): UseMutationResult<
  UpdateRoutingLoadSettingsResponse,
  Error,
  UpdateRoutingLoadSettingsRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => updateRoutingLoadSettings(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROUTING_LOAD_SETTINGS_KEY });
      void qc.invalidateQueries({ queryKey: ROUTING_LOAD_OVERVIEW_KEY });
    },
  });
}

export function useSkuRateLimits(): UseQueryResult<ListSkuRateLimitsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ROUTING_LOAD_SKU_RATE_LIMITS_KEY,
    queryFn: ({ signal }) => listSkuRateLimits(fetchImpl, signal),
  });
}

export function usePutSkuRateLimit(): UseMutationResult<
  PutSkuRateLimitResponse,
  Error,
  { readonly skuId: string; readonly body: PutRateLimitRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skuId, body }) => putSkuRateLimit(skuId, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROUTING_LOAD_SKU_RATE_LIMITS_KEY });
      void qc.invalidateQueries({ queryKey: ROUTING_LOAD_OVERVIEW_KEY });
      void qc.invalidateQueries({ queryKey: ['routing-load-effective'] });
      void qc.invalidateQueries({ queryKey: QUOTA_REFUSALS_KEY });
    },
  });
}

export function useDeleteSkuRateLimit(): UseMutationResult<
  DeleteRateLimitResponse,
  Error,
  { readonly skuId: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ skuId }) => deleteSkuRateLimit(skuId, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ROUTING_LOAD_SKU_RATE_LIMITS_KEY });
      void qc.invalidateQueries({ queryKey: ROUTING_LOAD_OVERVIEW_KEY });
      void qc.invalidateQueries({ queryKey: ['routing-load-effective'] });
      void qc.invalidateQueries({ queryKey: QUOTA_REFUSALS_KEY });
    },
  });
}

export function useOrgSkuRateLimits(orgId: string | null): UseQueryResult<ListOrgSkuRateLimitsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: orgId === null ? ['routing-load-rate-limits', 'org', ''] : routingLoadOrgRateLimitsKey(orgId),
    queryFn: ({ signal }) => listOrgSkuRateLimits(orgId ?? '', fetchImpl, signal),
    enabled: orgId !== null && orgId.length > 0,
  });
}

export function usePutOrgSkuRateLimit(): UseMutationResult<
  PutOrgSkuRateLimitResponse,
  Error,
  { readonly orgId: string; readonly skuId: string; readonly body: PutRateLimitRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, skuId, body }) => putOrgSkuRateLimit(orgId, skuId, body, fetchImpl),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: routingLoadOrgRateLimitsKey(variables.orgId) });
      void qc.invalidateQueries({ queryKey: QUOTA_ORG_SKU_ALL_KEY });
      void qc.invalidateQueries({ queryKey: ROUTING_LOAD_OVERVIEW_KEY });
      void qc.invalidateQueries({ queryKey: ['routing-load-effective'] });
      void qc.invalidateQueries({ queryKey: QUOTA_REFUSALS_KEY });
    },
  });
}

export function useDeleteOrgSkuRateLimit(): UseMutationResult<
  DeleteRateLimitResponse,
  Error,
  { readonly orgId: string; readonly skuId: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, skuId }) => deleteOrgSkuRateLimit(orgId, skuId, fetchImpl),
    onSuccess: (_data, variables) => {
      void qc.invalidateQueries({ queryKey: routingLoadOrgRateLimitsKey(variables.orgId) });
      void qc.invalidateQueries({ queryKey: QUOTA_ORG_SKU_ALL_KEY });
      void qc.invalidateQueries({ queryKey: ROUTING_LOAD_OVERVIEW_KEY });
      void qc.invalidateQueries({ queryKey: ['routing-load-effective'] });
      void qc.invalidateQueries({ queryKey: QUOTA_REFUSALS_KEY });
    },
  });
}

function invalidateScopeQueries(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ROUTING_LOAD_SCOPE_SETTINGS_KEY });
  void qc.invalidateQueries({ queryKey: ROUTING_LOAD_OVERVIEW_KEY });
  void qc.invalidateQueries({ queryKey: ROUTING_LOAD_SETTINGS_KEY });
}

export function useRoutingLoadScopeSettings(): UseQueryResult<ListRoutingLoadScopeSettingsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ROUTING_LOAD_SCOPE_SETTINGS_KEY,
    queryFn: ({ signal }) => listRoutingLoadScopeSettings(fetchImpl, signal),
  });
}

export function usePutRoutingLoadScopeSettings(): UseMutationResult<
  PutRoutingLoadScopeSettingsResponse,
  Error,
  {
    readonly kind: RoutingLoadScopeKind;
    readonly scopeId: string;
    readonly body: UpdateRoutingLoadScopeSettingsRequest;
  }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, scopeId, body }) => putRoutingLoadScopeSettings(kind, scopeId, body, fetchImpl),
    onSuccess: () => invalidateScopeQueries(qc),
  });
}

export function useDeleteRoutingLoadScopeSettings(): UseMutationResult<
  DeleteRateLimitResponse,
  Error,
  { readonly kind: RoutingLoadScopeKind; readonly scopeId: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ kind, scopeId }) => deleteRoutingLoadScopeSettings(kind, scopeId, fetchImpl),
    onSuccess: () => invalidateScopeQueries(qc),
  });
}

function invalidateQuotaQueries(qc: ReturnType<typeof useQueryClient>): void {
  void qc.invalidateQueries({ queryKey: ROUTING_LOAD_SKU_RATE_LIMITS_KEY });
  void qc.invalidateQueries({ queryKey: QUOTA_ORG_RATE_LIMITS_KEY });
  void qc.invalidateQueries({ queryKey: QUOTA_ORG_SKU_ALL_KEY });
  void qc.invalidateQueries({ queryKey: QUOTA_GLOBAL_RATE_LIMIT_KEY });
  void qc.invalidateQueries({ queryKey: QUOTA_BYPASS_KEY });
  void qc.invalidateQueries({ queryKey: ROUTING_LOAD_OVERVIEW_KEY });
  void qc.invalidateQueries({ queryKey: ROUTING_LOAD_SETTINGS_KEY });
  void qc.invalidateQueries({ queryKey: ['routing-load-effective'] });
  void qc.invalidateQueries({ queryKey: ['routing-load-rate-limits', 'org'] });
  void qc.invalidateQueries({ queryKey: QUOTA_REFUSALS_KEY });
}

export function useQuotaBypass(): UseQueryResult<QuotaBypassState> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: QUOTA_BYPASS_KEY,
    queryFn: ({ signal }) => getQuotaBypass(fetchImpl, signal),
    refetchInterval: 15_000,
  });
}

export function usePutQuotaBypass(): UseMutationResult<QuotaBypassState, Error, boolean> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (enabled) => putQuotaBypass(enabled, fetchImpl),
    onSuccess: () => invalidateQuotaQueries(qc),
  });
}

export function useGlobalRateLimit(): UseQueryResult<GetGlobalRateLimitResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: QUOTA_GLOBAL_RATE_LIMIT_KEY,
    queryFn: ({ signal }) => getGlobalRateLimit(fetchImpl, signal),
  });
}

export function usePutGlobalRateLimit(): UseMutationResult<
  PutGlobalRateLimitResponse,
  Error,
  PutRateLimitRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => putGlobalRateLimit(body, fetchImpl),
    onSuccess: () => invalidateQuotaQueries(qc),
  });
}

export function useDeleteGlobalRateLimit(): UseMutationResult<DeleteRateLimitResponse, Error, void> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => deleteGlobalRateLimit(fetchImpl),
    onSuccess: () => invalidateQuotaQueries(qc),
  });
}

export function useOrgAllRateLimits(): UseQueryResult<ListOrgRateLimitsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: QUOTA_ORG_RATE_LIMITS_KEY,
    queryFn: ({ signal }) => listOrgRateLimits(fetchImpl, signal),
  });
}

export function useAllOrgSkuRateLimits(): UseQueryResult<ListOrgSkuRateLimitsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: QUOTA_ORG_SKU_ALL_KEY,
    queryFn: ({ signal }) => listAllOrgSkuRateLimits(fetchImpl, signal),
  });
}

export function usePutOrgAllRateLimit(): UseMutationResult<
  PutOrgRateLimitResponse,
  Error,
  { readonly orgId: string; readonly body: PutRateLimitRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId, body }) => putOrgRateLimit(orgId, body, fetchImpl),
    onSuccess: () => invalidateQuotaQueries(qc),
  });
}

export function useDeleteOrgAllRateLimit(): UseMutationResult<
  DeleteRateLimitResponse,
  Error,
  { readonly orgId: string }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ orgId }) => deleteOrgRateLimit(orgId, fetchImpl),
    onSuccess: () => invalidateQuotaQueries(qc),
  });
}

export function useRateLimitRefusals(
  since: string,
  enabled = true,
): UseQueryResult<ListRateLimitRefusalsResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: [...QUOTA_REFUSALS_KEY, since],
    queryFn: ({ signal }) => listRateLimitRefusals(since, fetchImpl, signal),
    enabled,
  });
}

export function useEffectiveRateLimit(
  orgId: string,
  skuId: string,
  enabled: boolean,
): UseQueryResult<EffectiveRateLimitResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['routing-load-effective', 'sku', orgId, skuId],
    queryFn: ({ signal }) => getEffectiveRateLimit(orgId, skuId, fetchImpl, signal),
    enabled: enabled && orgId.length > 0 && skuId.length > 0,
  });
}

export function useOrgEffectiveRateLimits(
  orgId: string,
  enabled: boolean,
): UseQueryResult<OrgEffectiveRateLimitResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['routing-load-effective', 'org', orgId],
    queryFn: ({ signal }) => getOrgEffectiveRateLimits(orgId, fetchImpl, signal),
    enabled: enabled && orgId.length > 0,
  });
}

export function useAllTaskAdmissionOverrides(): UseQueryResult<ListAllOrgTaskAdmissionOverridesResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: QUOTA_TASK_ADMISSION_KEY,
    queryFn: ({ signal }) => listAllTaskAdmissionOverrides(fetchImpl, signal),
  });
}
